import Button from "shared/components/button";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import Size from "shared/enums/Size";
import Headshot from "../headshot";
import useUploadPictureService from "./service";
import UploadPictureProps, { CropImageProp } from "./type";
import ReactCrop from "react-image-crop";
import "react-image-crop/src/ReactCrop.scss";
import React from "react";
import { WebcamCapture } from "./captureImage";
import { useCopies } from "shared/hooks/useCopies";
import ButtonGroup from "shared/components/button/components/buttonGroup";

export default function UploadPicture(props: UploadPictureProps) {
  const service = useUploadPictureService(props.onClose, props.regParticipantId, props.uiVersion, props.onSuccess);
  const copies = useCopies();

  return (
    <Modal>
      <ModalHeader left={copies.get("edit.profile.photo")} onClose={props.onClose} />
      <ModalBody>
        <div className="profilePicUpload">
          {!service.state?.cropImage && !service.state?.captureImage ? (
            <React.Fragment>
              <Headshot imageLocation={props.imageLocation} size={Size.LARGE} nameInitials={props.nameInitials} />
              <div className="profilePicUpload_actions">
                <div className="profilePicUpload_actionsWrapper">
                  <label className="profilePicUpload_uploadBtn">
                    <input type="file" onChange={service.handleSelectImage} accept="image/png, image/jpeg" />
                    <Button label={copies.get("upload.photo")} />
                  </label>
                  <span className="profilePicUpload_actions--note">
                    {copies.get("supported.formats.profile.picture")}
                  </span>
                </div>
                {props.isUploadByCameraEnabled && (
                  <React.Fragment>
                    <span className="profilePicUpload_actions--or">{copies.get("or")}</span>
                    <Button
                      isOutline
                      label={copies.get("use.camera")}
                      onClick={() => {
                        service.handleUseCamera();
                      }}
                      disabled={service.state.browserSupportError}
                    />
                  </React.Fragment>
                )}
              </div>
            </React.Fragment>
          ) : null}
          {service.state?.captureImage ? (
            <WebcamCapture
              setCapturedImage={service.setCapturedImage}
              handleError={service.handleCameraError}
              handleCancel={service.handleUploadCancel}
              handleBrowserSupport={service.handleBroswerSupport}
            />
          ) : null}
          {service.state?.cropImage ? (
            <CropImage
              cropState={service.cropState}
              onCropComplete={service.onCropComplete}
              setCropState={service.setCropState}
              handleUpload={service.handleUpload}
              handleUploadCancel={service.handleUploadCancel}
              centerImageOnLoad={service.centerImageOnLoad}
              state={service.state}
            />
          ) : null}

          <p className="profilePicUpload_error">
            <span>{service.state.error}</span>
            {service.state.cameraError && <span>{copies.get("unable.to.access.camera")}</span>}
            {service.state.browserSupportError && <span>{copies.get("no.browser.support")}</span>}
          </p>
        </div>
      </ModalBody>
    </Modal>
  );
}

function CropImage(props: CropImageProp) {
  const copies = useCopies();

  return (
    <React.Fragment>
      <div className="profilePicUpload_crop">
        <ReactCrop
          ruleOfThirds
          circularCrop
          crop={props.cropState}
          aspect={1 / 1}
          minHeight={200}
          minWidth={200}
          keepSelection
          onComplete={props.onCropComplete}
          onChange={crop => {
            props.setCropState(crop);
          }}>
          <img src={props.state?.imageSrc} alt="CropImage" onLoad={props.centerImageOnLoad} />
        </ReactCrop>
      </div>
      <ButtonGroup>
        <Button label={copies.get("set.profile.photo")} onClick={props.handleUpload} />
        <Button label={copies.get("cancel")} onClick={props.handleUploadCancel} isOutline />
      </ButtonGroup>
    </React.Fragment>
  );
}
